import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronDown, Loader2, Sparkles, Square, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { adminAiModels, adminAiUrl } from "@/lib/api";
import { streamSse } from "@/lib/sse";
import { cn } from "@/lib/utils";

const TONES = [{ value: "varm", label: "Varm" }, { value: "noytral", label: "Nøytral" }, { value: "festleg", label: "Festleg" }, { value: "hogtideleg", label: "Høgtideleg" }];
const LENGTHS = [{ value: "kort", label: "Kort" }, { value: "middels", label: "Middels" }, { value: "lang", label: "Lang" }];
const BODY_ACTIONS = [{ id: "improve", label: "Forbetre språket" }, { id: "shorten", label: "Kortare" }, { id: "expand", label: "Lengre" }, { id: "nynorsk", label: "Nynorsk-vask" }];
const FIELD_ACTIONS = [{ id: "title", label: "Nye titlar" }, { id: "lead", label: "Ny ingress" }, { id: "tags", label: "Finn stikkord" }, { id: "seo", label: "SEO-tekst" }];
const MODEL_KEY = "hk-writer-model";
const words = (html) => (html || "").replace(/<[^>]+>/g, " ").split(/\s+/).filter(Boolean).length;

export const streamAssist = (body, onDelta, signal) => streamSse(adminAiUrl("assist"), { ...body, model: localStorage.getItem(MODEL_KEY) || undefined }, onDelta, signal);

/** Splits the streamed draft into its fields: TITTEL/INGRESS/STIKKORD/SEO header lines, body HTML after the --- line. */
export const parseDraft = (text) => {
  const out = {};
  const [head, ...rest] = text.split(/\n---\s*\n?/);
  const hasBody = rest.length > 0;
  const grab = (k) => { const m = head.match(new RegExp(`^${k}:\\s*(.*)$`, "m")); return m ? m[1].trim() : undefined; };
  const title = grab("TITTEL"), lead = grab("INGRESS"), tags = grab("STIKKORD"), seo = grab("SEO");
  if (title && (hasBody || /\nINGRESS:/.test(head))) out.title = title;
  if (lead && (hasBody || /\nSTIKKORD:/.test(head))) out.lead = lead.replace(/^["«]|["»]$/g, "");
  if (tags && (hasBody || /\nSEO:/.test(head))) out.tags = tags.split(",").map((t) => t.trim().toLowerCase()).filter(Boolean);
  if (seo && hasBody) out.seo = seo;
  if (hasBody) out.body = rest.join("\n---\n").replace(/^```html\s*/i, "").replace(/```\s*$/, "").trim();
  return out;
};

const DarkSeg = ({ value, onChange, items, testId }) => (
  <div className="ai-seg" role="radiogroup" data-testid={testId}>
    {items.map((it) => <button key={it.value} type="button" role="radio" aria-checked={value === it.value} onClick={() => onChange(it.value)} className={cn(value === it.value && "is-on")} data-testid={`${testId}-${it.value}`}>{it.label}</button>)}
  </div>
);

/** Which of the newest models writes – remembered in the browser. */
const ModelPicker = ({ value, onChange }) => {
  const { data } = useQuery({ queryKey: ["ai-models"], queryFn: adminAiModels, staleTime: 600_000 });
  const models = data?.models || [];
  const current = models.find((m) => m.id === value) || models.find((m) => m.id === data?.default) || models[0];
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="ai-model-trigger" title="Vel AI-modell" data-testid="ai-model-trigger"><span className="ai-dot" aria-hidden="true" />{current?.label || "Modell"}<ChevronDown className="h-3 w-3 opacity-60" /></button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[290px] p-1.5" data-testid="ai-model-menu">
        <p className="px-2.5 pb-1 pt-1.5 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Kven skal skrive?</p>
        <ul>
          {models.map((m) => (
            <li key={m.id}>
              <button type="button" onClick={() => onChange(m.id)} className={cn("flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors hover:bg-secondary", current?.id === m.id && "bg-secondary")} data-testid={`ai-model-${m.id}`}>
                <Check className={cn("mt-1 h-3.5 w-3.5 shrink-0", current?.id === m.id ? "opacity-100" : "opacity-0")} />
                <span className="min-w-0 flex-1"><span className="flex items-center gap-2 text-[13.5px] font-medium">{m.label}{m.badge && <span className="rounded-full bg-sol/20 px-1.5 py-px text-[10px] font-semibold uppercase tracking-wider text-ink dark:text-sol">{m.badge}</span>}</span><span className="block text-[12px] text-muted-foreground">{m.hint}</span></span>
              </button>
            </li>
          ))}
        </ul>
        {data && !data.configured && <p className="px-2.5 py-2 text-[12px] text-destructive" data-testid="ai-model-unconfigured">AI-nøkkelen manglar på serveren.</p>}
      </PopoverContent>
    </Popover>
  );
};

/**
 * «Skriv med AI»: keywords in, a whole article out – streamed live into the fields and the editor. Then small helpers
 * for the text that is already there. Everything is a suggestion the editor can undo (Angre) or overwrite.
 * `prefill` ({keywords, nonce, run}) lets the start screen hand over keywords and kick off a draft.
 */
export const AiWriter = ({ form, patch, editor, onBusy, prefill }) => {
  const [keywords, setKeywords] = useState(form.ai_keywords || "");
  const [tone, setTone] = useState("varm");
  const [length, setLength] = useState("middels");
  const [audience, setAudience] = useState("");
  const [model, setModelState] = useState(() => localStorage.getItem(MODEL_KEY) || "");
  const [busy, setBusy] = useState(null);
  const [progress, setProgress] = useState({ words: 0, model: "" });
  const [titles, setTitles] = useState(null);
  const ctrl = useRef(null);
  const undoRef = useRef(null);
  const kwRef = useRef(null);
  const setModel = (m) => { setModelState(m); localStorage.setItem(MODEL_KEY, m); };

  const run = async (kind, body, onDelta) => {
    ctrl.current?.abort();
    ctrl.current = new AbortController();
    setBusy(kind); onBusy?.(true, kind); setProgress({ words: 0, model: "" });
    try {
      return await streamSse(adminAiUrl(kind === "draft" ? "draft" : "assist"), { ...body, model: model || undefined }, onDelta, ctrl.current.signal, (ev) => setProgress((p) => ({ ...p, model: ev.model })));
    } catch (e) {
      if (e.name !== "AbortError") toast.error(e.message || "AI-skrivaren feila");
      return null;
    } finally { setBusy(null); onBusy?.(false, kind); }
  };
  const stop = () => ctrl.current?.abort();

  const draft = async (kw = keywords) => {
    if ((kw || "").trim().length < 3) { toast.error("Skriv nokre stikkord først"); return; }
    undoRef.current = { title: form.title, lead: form.lead, body: form.body, tags: form.tags, seo_description: form.seo_description };
    patch({ ai_keywords: kw });
    let last = 0;
    const apply = (all, final) => {
      const d = parseDraft(all);
      const p = {};
      if (d.title) p.title = d.title;
      if (d.lead) p.lead = d.lead;
      if (d.tags) p.tags = d.tags;
      if (d.seo) p.seo_description = d.seo;
      if (d.body !== undefined) {
        setProgress((pr) => ({ ...pr, words: words(d.body) }));
        if (final || Date.now() - last > 160) { last = Date.now(); p.body = d.body; editor?.commands.setContent(d.body, false); }
      }
      if (Object.keys(p).length) patch(p);
    };
    const all = await run("draft", { keywords: kw, tone, length, audience: audience || undefined, title: form.title || undefined }, (_, acc) => apply(acc, false));
    if (all != null) {
      apply(all, true);
      const n = words(parseDraft(all).body);
      toast.success(`Utkastet er klart – ${n} ord`, { duration: 8000, description: "Les gjennom, juster, og lagre når du er nøgd.", action: { label: "Angre", onClick: () => { const u = undoRef.current; if (u) { patch(u); editor?.commands.setContent(u.body || "", false); } } } });
    }
  };

  useEffect(() => {
    if (!prefill?.nonce) return;
    setKeywords(prefill.keywords || "");
    if (prefill.run) draft(prefill.keywords); else setTimeout(() => kwRef.current?.focus(), 80);
  }, [prefill?.nonce]); // eslint-disable-line react-hooks/exhaustive-deps

  const assistBody = async (action) => {
    const text = form.body || "";
    if (!text.trim()) { toast.error("Skriv eller generer tekst først"); return; }
    const before = text;
    let last = 0;
    const all = await run(action, { action, text, context: [form.title, form.lead].filter(Boolean).join(" – ") }, (_, acc) => {
      setProgress((pr) => ({ ...pr, words: words(acc) }));
      if (Date.now() - last > 160) { last = Date.now(); editor?.commands.setContent(acc, false); }
    });
    if (all != null) {
      const html = all.replace(/^```html\s*/i, "").replace(/```\s*$/, "").trim();
      editor?.commands.setContent(html, false); patch({ body: html });
      toast.success("Teksten er oppdatert", { duration: 8000, action: { label: "Angre", onClick: () => { editor?.commands.setContent(before, false); patch({ body: before }); } } });
    }
  };

  const assistField = async (action) => {
    const text = form.body || form.lead || form.title || "";
    if (!text.trim()) { toast.error("Skriv eller generer tekst først"); return; }
    const all = await run(action, { action, text: `${form.title ? `Tittel: ${form.title}\n` : ""}${form.lead ? `Ingress: ${form.lead}\n\n` : ""}${form.body || ""}` });
    if (all == null) return;
    const clean = all.trim();
    if (action === "title") setTitles(clean.split("\n").map((l) => l.replace(/^[\d.\-•*\s]+/, "").trim()).filter(Boolean).slice(0, 5));
    else if (action === "lead") patch({ lead: clean.replace(/^["«]|["»]$/g, "") });
    else if (action === "tags") patch({ tags: clean.split(",").map((t) => t.trim().toLowerCase()).filter(Boolean).slice(0, 8) });
    else if (action === "seo") patch({ seo_description: clean.slice(0, 160) });
  };

  const streaming = Boolean(busy);
  return (
    <section className={cn("ai-studio", streaming && "is-busy")} data-testid="ai-writer" aria-busy={streaming}>
      <span className="ai-shimmer" aria-hidden="true" />
      <header className="flex items-start justify-between gap-3">
        <div><h2 className="flex items-center gap-2 font-display text-[15px] font-semibold tracking-[-0.01em]"><Sparkles className="h-4 w-4 text-sol" /> Skriv med AI</h2><p className="mt-1 text-[12.5px] leading-snug text-paper/60">Stikkord inn – ferdig formatert sak ut, på nynorsk. Alt kan endrast etterpå.</p></div>
        <ModelPicker value={model} onChange={setModel} />
      </header>

      <div className="mt-5 space-y-4">
        <div>
          <label htmlFor="ai-keywords" className="ai-label">Stikkord eller notat</label>
          <textarea ref={kwRef} id="ai-keywords" value={keywords} onChange={(e) => setKeywords(e.target.value)} rows={5} placeholder={"t.d. haustkonsert med Strupebandet 14. november, gjestekor frå Stord, kaffi og kaker i Kippers, billettar 250 kr, dørene opnar 18:30, overskot går til nytt lydanlegg"} className="ai-input mt-1.5 resize-y" data-testid="ai-keywords-input" />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div><p className="ai-label">Tone</p><DarkSeg value={tone} onChange={setTone} items={TONES} testId="ai-tone" /></div>
          <div><p className="ai-label">Lengd</p><DarkSeg value={length} onChange={setLength} items={LENGTHS} testId="ai-length" /></div>
        </div>
        <input value={audience} onChange={(e) => setAudience(e.target.value)} placeholder="Målgruppe (valfritt) – t.d. barnefamiliar, hyttefolk, lag og foreiningar" className="ai-input h-10" data-testid="ai-audience-input" />
        {streaming ? (
          <div className="ai-progress" role="status" data-testid="ai-progress">
            <span className="ai-caret" aria-hidden="true" />
            <span className="min-w-0 flex-1 truncate text-[13px]">{busy === "draft" ? "AI skriv saka" : "AI arbeider med teksten"}{progress.model ? ` · ${progress.model}` : ""}{progress.words ? <span className="tabular text-paper/60"> · {progress.words} ord</span> : null}</span>
            <button type="button" onClick={stop} className="ai-stop" data-testid="ai-stop-button"><Square className="h-3 w-3" /> Stopp</button>
          </div>
        ) : (
          <button type="button" className="ai-primary" onClick={() => draft()} data-testid="ai-draft-button"><Wand2 className="h-4 w-4" /> {form.body ? "Skriv nytt utkast med AI" : "Lag utkast med AI"}</button>
        )}
      </div>

      <div className="mt-6 border-t border-paper/10 pt-5">
        <p className="ai-label">Hjelp med teksten du har</p>
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="ai-body-actions">
          {BODY_ACTIONS.map((a) => <button key={a.id} type="button" className="ai-chip" onClick={() => assistBody(a.id)} disabled={streaming} data-testid={`ai-action-${a.id}`}>{busy === a.id && <Loader2 className="h-3 w-3 animate-spin" />}{a.label}</button>)}
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="ai-field-actions">
          {FIELD_ACTIONS.map((a) => <button key={a.id} type="button" className="ai-chip is-ghost" onClick={() => assistField(a.id)} disabled={streaming} data-testid={`ai-action-${a.id}`}>{busy === a.id && <Loader2 className="h-3 w-3 animate-spin" />}{a.label}</button>)}
        </div>
        {titles && (
          <ul className="mt-3 space-y-1 rounded-xl bg-paper/[0.06] p-1.5" data-testid="ai-title-options">
            {titles.map((t) => <li key={t}><button type="button" onClick={() => { patch({ title: t }); setTitles(null); }} className={cn("flex w-full items-start gap-2 rounded-lg px-2.5 py-2 text-left text-[13.5px] transition-colors hover:bg-paper/10", t === form.title && "bg-paper/10")} data-testid="ai-title-option"><Check className={cn("mt-0.5 h-3.5 w-3.5 shrink-0 text-sol", t === form.title ? "opacity-100" : "opacity-0")} />{t}</button></li>)}
            <li><button type="button" onClick={() => setTitles(null)} className="px-2.5 py-1 text-[12px] text-paper/60 hover:text-paper">Lukk forslaga</button></li>
          </ul>
        )}
      </div>
    </section>
  );
};
